import { format } from 'date-fns'

import { Modal } from 'components/UI/Modal/Modal'
import { Editor } from 'components/Editor/Editor'
import type { Reply } from '../sequences_api'

interface ReplyViewModalProps {
  reply: Reply | null
  show: boolean
  onClose: () => void
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'PPpp') // Format: Apr 29, 2023, 1:30:00 PM
  } catch {
    return dateStr
  }
}

export function ReplyViewModal({ reply, show, onClose }: ReplyViewModalProps) {
  if (!reply) return null

  return (
    <Modal
      size="xl"
      open={show}
      onOpenChange={(open) => !open && onClose()}
      title={reply.subject || '(no subject)'}
      onAccept={onClose}
      acceptButtonText="Close"
      hideCancelButton
    >
      <div className="space-y-4">
        {/* Header info */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <div className="text-gray-700">
              <strong>From:</strong>{' '}
              {reply.from_name || reply.from_email_address}
              {reply.from_name && (
                <span className="text-gray-500 ml-1">
                  &lt;{reply.from_email_address}&gt;
                </span>
              )}
            </div>
            <div className="text-gray-500">
              {formatDateTime(reply.date_received)}
            </div>
          </div>
          <div className="text-gray-700">
            <strong>To:</strong>{' '}
            {reply.sender_email_name || reply.primary_to_email_address}
            {reply.sender_email_name && (
              <span className="text-gray-500 ml-1">
                &lt;{reply.primary_to_email_address}&gt;
              </span>
            )}
          </div>
          {reply.lead_full_name && (
            <div className="text-gray-700">
              <strong>Lead:</strong> {reply.lead_full_name}
              {reply.lead_email && (
                <span className="text-gray-500 ml-1">
                  &lt;{reply.lead_email}&gt;
                </span>
              )}
            </div>
          )}
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              reply.is_read
                ? 'bg-gray-100 text-gray-700'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {reply.is_read ? 'Read' : 'Unread'}
          </span>
          {reply.is_interested && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Interested
            </span>
          )}
          {reply.is_automated_reply && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              Auto-reply
            </span>
          )}
          {reply.is_tracked_reply && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
              Tracked Reply
            </span>
          )}
        </div>

        {/* Email content */}
        <div className="min-h-[200px] max-h-[400px] overflow-y-auto p-2 border rounded">
          {reply.html_body ? (
            <Editor value={reply.html_body} readOnly disableToolbar />
          ) : reply.text_body ? (
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
              {reply.text_body}
            </pre>
          ) : (
            <p className="text-gray-500 italic">No content</p>
          )}
        </div>

        {/* Attachments */}
        {reply.attachments && reply.attachments.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2">
              Attachments ({reply.attachments.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {reply.attachments.map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                  {attachment.file_name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
